import {
  LayoutDashboard,
  Users,
  UserCheck,
  MessageSquare,
  Star,
  Image,
  FileText,
  ShoppingBag,
  Settings,
  Shield,
  ScrollText,
  AlertTriangle,
  Info,
  Bell,
  Mail,
  Crown,
  Ticket,
  Dumbbell,
  CheckCircle2,
  Code,
  MapPin,
  Globe,
  CreditCard,
  DollarSign,
  Video,
  Calendar,
  Trophy,
  Megaphone,
  Gift,
} from 'lucide-react';

/**
 * Build the admin sidebar menu definition.
 * Badge values are passed in from the sidebar's live counts so the config
 * stays a pure function of its inputs (extracted from AdminSidebar.jsx).
 */
export const getAdminMenuItems = ({
  pendingTrainersCount = 0,
  reportedMessagesCount = 0,
  reportedTrainersCount = 0,
  criticalTrainersCount = 0,
  lastViewedWarningsCount = 0,
  unreadTicketsCount = 0,
  advertisingInquiriesCount = 0,
  pendingReviewsCount = 0,
} = {}) => [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Overview & Stats',
  },
  {
    id: 'trainers',
    label: 'Trainer List',
    icon: UserCheck,
    description: 'Trainer Management',
    badge: pendingTrainersCount,
  },
  {
    id: 'customers',
    label: 'Customer List',
    icon: Users,
    description: 'Customer Management',
  },
  {
    id: 'reported-messages',
    label: 'Reported Messages',
    icon: MessageSquare,
    description: 'User Reports',
    badge: reportedMessagesCount,
  },
  {
    id: 'reported-trainers',
    label: 'Reported Trainers',
    icon: Users,
    description: 'Trainer Reports',
    badge: reportedTrainersCount,
  },
  {
    id: 'trainer-warnings',
    label: 'Trainer Report Warnings',
    icon: AlertTriangle,
    description: '3+ Reports (Warning Level)',
    badge: criticalTrainersCount > lastViewedWarningsCount ? criticalTrainersCount : 0,
    badgeVariant: 'critical',
  },
  {
    id: 'ip-logs',
    label: 'IP Logs',
    icon: Shield,
    description: 'Security & IP Tracking',
  },
  {
    id: 'support-tickets',
    label: 'Support Tickets',
    icon: Mail,
    description: 'Help & Support Requests',
    badge: unreadTicketsCount,
  },
  {
    id: 'advertising-inquiries',
    label: 'Advertising Inquiries',
    icon: Megaphone,
    description: 'Ad Partnership Requests',
    badge: advertisingInquiriesCount,
  },
  {
    id: 'advertisements',
    label: 'Advertisements',
    icon: Image,
    description: 'Manage Active Ads',
  },
  {
    id: 'faq',
    label: 'FAQ',
    icon: Info,
    description: 'Manage FAQ Page',
  },
  {
    id: 'contact-info',
    label: 'Contact Page',
    icon: Settings,
    description: 'Edit Contact Info',
  },
  {
    id: 'reviews',
    label: 'Reviews',
    icon: Star,
    description: 'Moderation',
    badge: pendingReviewsCount,
  },
  {
    id: 'blog',
    label: 'Blog',
    icon: FileText,
    description: 'Posts & Categories',
  },
  {
    id: 'comments',
    label: 'Blog Comments',
    icon: MessageSquare,
    description: 'Moderate Comments',
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    icon: Mail,
    description: 'Email Subscribers',
  },
  {
    id: 'featured-video',
    label: 'Featured Video',
    icon: Video,
    description: 'Product Showcase Video',
  },
  {
    id: 'shop',
    label: 'Shop',
    icon: ShoppingBag,
    description: 'Products & Orders',
  },
  {
    id: 'restock-requests',
    label: 'Restock Requests',
    icon: Bell,
    description: 'Customer Demand',
  },
  {
    id: 'memberships',
    label: 'Boost Package',
    icon: Crown,
    description: 'Trainer Boost Packages',
  },
  {
    id: 'boost-categories',
    label: 'Boost Categories',
    icon: Settings,
    description: 'Package Category Settings',
  },
  {
    id: 'achievements',
    label: 'Achievements',
    icon: Trophy,
    description: 'Trainer Achievement Badges',
  },
  {
    id: 'referrals',
    label: 'Referrals',
    icon: Gift,
    description: 'Trainer Referral System',
  },
  {
    id: 'listing-credits',
    label: 'Listing Credits',
    icon: Calendar,
    description: 'Homepage Listing Credits',
  },
  {
    id: 'credit-packages',
    label: 'Credit Packages',
    icon: CreditCard,
    description: 'Manage Credit Packages',
  },
  {
    id: 'coupons',
    label: 'Coupon List',
    icon: Ticket,
    description: 'Discount Coupons',
  },
  {
    id: 'proficiency-setting',
    label: 'Proficiency Setting',
    icon: Dumbbell,
    description: 'Sport Specializations',
  },
  {
    id: 'type-setting',
    label: 'Type Setting',
    icon: CheckCircle2,
    description: 'Training Session Types',
  },
  {
    id: 'footer',
    label: 'Footer',
    icon: Settings,
    description: 'Site Footer',
  },
  {
    id: 'about-us',
    label: 'About Us Page',
    icon: Info,
    description: 'About Us Content',
  },
  {
    id: 'privacy-policy',
    label: 'Privacy Policy',
    icon: Shield,
    description: 'Legal Content',
  },
  {
    id: 'legal-disclaimer',
    label: 'Payment Disclaimer',
    icon: AlertTriangle,
    description: 'Payment Legal Notice',
  },
  {
    id: 'payment-info',
    label: 'Payment Info Tab',
    icon: DollarSign,
    description: 'User/Trainer Dashboard Notice',
  },
  {
    id: 'terms-conditions',
    label: 'Terms & Conditions',
    icon: ScrollText,
    description: 'Terms of Use',
  },
  {
    id: 'disclaimer',
    label: 'Disclaimer',
    icon: AlertTriangle,
    description: 'Disclosure Statement',
  },
  {
    id: 'email-templates',
    label: 'Email Templates',
    icon: Mail,
    description: 'Automated Emails',
  },
  {
    id: 'page-settings',
    label: 'Page Settings',
    icon: FileText,
    description: 'Footer, Social, Pages',
  },
  {
    id: 'community-page',
    label: 'Community Page',
    icon: Users,
    description: 'Join the Community',
  },
  {
    id: 'settings',
    label: 'Site Settings',
    icon: Settings,
    description: 'General Settings',
  },
  {
    id: 'google-tag-manager',
    label: 'Google Tag Manager',
    icon: Code,
    description: 'GTM & Analytics',
  },
  {
    id: 'city-settings',
    label: 'City Settings',
    icon: MapPin,
    description: 'Manage Cities',
  },
  {
    id: 'country-settings',
    label: 'Country Settings',
    icon: Globe,
    description: 'Manage Countries',
  },
  {
    id: 'payment-history',
    label: 'Payment History',
    icon: CreditCard,
    description: 'Orders & Memberships',
  },
  {
    id: 'payments',
    label: 'PayPal Payments',
    icon: DollarSign,
    description: 'Real-time Payment Tracking',
  },
  {
    id: 'account-settings',
    label: 'Account Settings',
    icon: Shield,
    description: 'Admin Credentials & Security',
  },
];
